import { Center, Flex, Spinner, Text, Dialog, CloseButton } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useCallback, useState, type MouseEvent } from "react";

import { useQuery } from "@tanstack/react-query";
import { Button } from "@digital-services-gard-as/anchor-react";
import { getClaimsMemberQueryOptions } from "@/api/queryOptions/claimsApiQueryOptions";
import { toaster } from "@/components/ui/toaster";
import { createInvoiceAddressText } from "@/utils/addressUtils";
import { isApiError } from "@/utils/errorUtils";
import { CopyIcon } from "@digital-services-gard-as/anchor-icons";

type InvoiceAddressModalProps = {
    claimIdRef: string;
};

const NOT_FOUND_STATUS = 404;
const TOAST_DURATION = 5000;

export const InvoiceAddressModal = ({ claimIdRef }: InvoiceAddressModalProps) => {
    const { t } = useTranslation(["errorMessages", "claimsInvoicesOverview"]);
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const queryOptions = getClaimsMemberQueryOptions(claimIdRef);
    const { data, isFetching, error } = useQuery({
        ...queryOptions,
        enabled: isOpen
    });
    const address = data || isNotFoundError(error) ? createInvoiceAddressText(data) : "";

    const handleOpenInvoiceAddressModal = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(true);
    }, []);

    const handleCopyInvoiceClick = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            navigator.clipboard.writeText(address);
            toaster.create({
                id: "invoiceAddressCopied",
                title: t("claimsInvoicesOverview:invoiceAddressModal.copyToast"),
                type: "success",
                duration: TOAST_DURATION,
                closable: true
            });
            setIsOpen(false);
        },
        [address, t]
    );

    const handleCloseInvoiceAddressModal = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setIsOpen(false);
    }, []);

    const handleOpenChange = useCallback((e: { open: boolean }) => {
        setIsOpen(e.open);
    }, []);

    const showAddress = () => {
        if (isFetching) {
            return (
                <Center>
                    <Flex flexDir="column" alignItems="center">
                        <Spinner />
                        <Text>{t("claimsInvoicesOverview:invoiceAddressModal.retrievingAddress")}</Text>
                    </Flex>
                </Center>
            );
        }

        if (error && !isNotFoundError(error)) {
            return <Text>{t("errorMessages:invoiceAddressModal.noAddress")}</Text>;
        }

        return (
            <Flex flexDir="column" gap="1rem">
                <Text>{t("claimsInvoicesOverview:invoiceAddressModal.description")}</Text>
                <Flex flexDir="column">
                    <Text whiteSpace="pre-wrap" data-testid="invoiceAddress">
                        {address}
                    </Text>
                </Flex>
                <Flex gap="1rem" mb="1rem" alignItems="start">
                    <Button id="copyInvoiceAddressBtn" onClick={handleCopyInvoiceClick}>
                        {t("claimsInvoicesOverview:invoiceAddressModal.copyBtn")}
                    </Button>
                    <Button id="cancelBtn" variant="secondary" onClick={handleCloseInvoiceAddressModal}>
                        {t("claimsInvoicesOverview:invoiceAddressModal.cancelBtn")}
                    </Button>
                </Flex>
            </Flex>
        );
    };

    return (
        <Dialog.Root open={isOpen} id="invoiceAddressModal" onOpenChange={handleOpenChange}>
            <Dialog.Trigger asChild>
                <Button
                    id={"copyBtn" + claimIdRef}
                    variant="icon-only"
                    icon={<CopyIcon />}
                    size="sm"
                    aria-label={t("claimsTable.copyInvoiceAddress")}
                    data-claim-id={claimIdRef}
                    tabIndex={0}
                    onClick={handleOpenInvoiceAddressModal}
                />
            </Dialog.Trigger>

            <Dialog.Backdrop />
            <Dialog.Positioner>
                <Dialog.Content data-testid="invoiceAddressModal">
                    <Dialog.CloseTrigger asChild>
                        <CloseButton size="sm" onClick={handleCloseInvoiceAddressModal} />
                    </Dialog.CloseTrigger>
                    <Dialog.Header>
                        <Dialog.Title fontSize="anchor.7">
                            {t("claimsInvoicesOverview:invoiceAddressModal.title")}
                        </Dialog.Title>
                    </Dialog.Header>
                    <Dialog.Body color="anchor.gray.900" fontSize="md">
                        {showAddress()}
                    </Dialog.Body>
                </Dialog.Content>
            </Dialog.Positioner>
        </Dialog.Root>
    );
};

const isNotFoundError = (error: unknown) => {
    if (!error) {
        return false;
    }
    if (isApiError(error)) {
        return error.status === NOT_FOUND_STATUS;
    }
    return false;
};
